import { UTCDate } from '@date-fns/utc';

export type TimeWindow = 'today' | 'next3' | 'week' | 'custom';

export const TIME_WINDOWS: { id: TimeWindow; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'next3', label: 'Next 3 days' },
  { id: 'week', label: 'This week' },
  { id: 'custom', label: 'Custom' },
];

const startOfDay = (date: UTCDate): UTCDate => {
  const next = new UTCDate(date);
  next.setUTCHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date: UTCDate): UTCDate => {
  const next = new UTCDate(date);
  next.setUTCHours(23, 59, 59, 999);
  return next;
};

const addDays = (date: UTCDate, days: number): UTCDate => {
  const next = new UTCDate(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

export type CustomDateRange = {
  startDate?: string;
  endDate?: string;
};

// Windows are UTC wall-clock, rolling from "now" (CLAUDE.md UTC rule). Week is
// the ISO Monday–Sunday range containing today. Custom uses the URL-stored
// range (XT-380); if incomplete, falls back to Today so the page never issues
// an unbounded list query.
export const timeWindowDates = (
  win: TimeWindow,
  now: UTCDate,
  custom?: CustomDateRange
): { startDate: string; endDate: string } => {
  if (win === 'custom' && custom?.startDate && custom?.endDate) {
    return { startDate: custom.startDate, endDate: custom.endDate };
  }

  const start = startOfDay(now);
  if (win === 'today' || win === 'custom') {
    // Incomplete custom → Today (safe default).
    return {
      startDate: start.toISOString(),
      endDate: endOfDay(now).toISOString(),
    };
  }
  if (win === 'next3') {
    return {
      startDate: start.toISOString(),
      endDate: endOfDay(addDays(start, 2)).toISOString(),
    };
  }
  const isoDayOfWeek = (start.getUTCDay() + 6) % 7; // 0 = Monday
  const monday = addDays(start, -isoDayOfWeek);
  return {
    startDate: monday.toISOString(),
    endDate: endOfDay(addDays(monday, 6)).toISOString(),
  };
};

// Which preset (if any) the current start/end date pair corresponds to.
// Custom ranges never match a preset and return undefined (caller keeps
// `window=custom` from the URL).
export const matchTimeWindow = (
  startDate: string | undefined,
  endDate: string | undefined,
  now: UTCDate
): Exclude<TimeWindow, 'custom'> | undefined => {
  if (!startDate || !endDate) return undefined;
  const presets = ['today', 'next3', 'week'] as const;
  return presets.find((win) => {
    const dates = timeWindowDates(win, now);
    return dates.startDate === startDate && dates.endDate === endDate;
  });
};
